import {
  PageShell,
} from "@/shared/components/pageShell";
/**
 * Rate Cards — named price cards with Local / Shared / Exclusive / Access /
 * Extras / Addons slices. ADR-008 fields live inside those tabs (no ADR tab).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  ExperimentOutlined,
  CopyOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import {
  fetchRateCardGroups,
  fetchGroupRows,
  saveGroupRows,
  setEditingRateCard,
  setLiveRateCard,
  duplicateRateCard,
} from "../services/rateCardsService.js";
import TestPricingDrawer from "../components/TestPricingDrawer.jsx";
import SeasonOpsBrief from "../components/SeasonOpsBrief.jsx";
import LivePriceCardElementsPanel from "../components/LivePriceCardElementsPanel.jsx";

const { Title, Text, Paragraph } = Typography;

/** Frontend-only Pricing tab for the live element contract (not a rate-card group API id). */
export const LIVE_ELEMENTS_GROUP_ID = "live_elements";

const LIVE_ELEMENTS_GROUP = Object.freeze({
  id: LIVE_ELEMENTS_GROUP_ID,
  name: "Live elements",
  methodLabel: "PWA, quotes & invoices",
  editable: false,
});

const GroupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupButton = styled.button`
  text-align: left;
  border: 1px solid ${(p) => (p.$active ? "#1677ff" : "#f0f0f0")};
  background: ${(p) => (p.$active ? "#e6f4ff" : "#fff")};
  border-radius: 8px;
  padding: 12px 14px;
  cursor: pointer;
`;

function rowKey(row, index) {
  return row._key || row.id || row.size || row.settingKey || `row-${index}`;
}

function newSharedRow() {
  return {
    _key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    id: "",
    originRegion: "GP",
    destinationRegion: "WC",
    serviceType: "shared_load",
    season: "",
    direction: "",
    ratePerM3: 0,
    minimumAmount: 0,
    enabled: true,
  };
}

function newExclusiveRow() {
  return {
    _key: `ex-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    id: "",
    originRegion: "GP",
    destinationRegion: "WC",
    truckSize: "8t",
    flatAmount: 0,
    minimumAmount: 0,
    enabled: true,
  };
}

function newBandRow() {
  return {
    _key: `band-${Date.now()}`,
    id: "",
    size: "",
    label: "",
    customerLabel: "",
    usableM3: 0,
    maxBillableM3: null,
    baseAmount: 0,
    includedDrivers: 1,
    includedHelpers: 0,
    enabled: true,
  };
}

function newAdr008RouteRow() {
  return {
    _key: `adr008-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    id: "",
    originRegion: "GP",
    destinationRegion: "WC",
    season: "standard",
    rate: 0,
    tariffBasis: "",
    mileageRate: 0,
    depoToDepoAmount: null,
    enabled: true,
  };
}

function TruckRateMapEditor({ value, onChange, testId, placeholder = "6_ton" }) {
  const [draftKey, setDraftKey] = React.useState("");
  const [draftRate, setDraftRate] = React.useState(null);
  const entries = Object.entries(value && typeof value === "object" ? value : {});

  const setEntry = (key, rate) => {
    onChange({ ...(value || {}), [key]: rate });
  };
  const removeEntry = (key) => {
    const next = { ...(value || {}) };
    delete next[key];
    onChange(next);
  };
  const addEntry = () => {
    const key = String(draftKey || "").trim();
    const rate = Number(draftRate);
    if (!key || !Number.isFinite(rate) || rate < 0) return;
    onChange({ ...(value || {}), [key]: rate });
    setDraftKey("");
    setDraftRate(null);
  };

  return (
    <Space direction="vertical" size={8} data-testid={testId} style={{ width: "100%" }}>
      {entries.map(([key, rate]) => (
        <Space key={key} wrap>
          <Text code>{key}</Text>
          <InputNumber
            min={0}
            value={rate}
            onChange={(v) => setEntry(key, v ?? 0)}
          />
          <Button type="text" danger onClick={() => removeEntry(key)}>
            Remove
          </Button>
        </Space>
      ))}
      <Space wrap>
        <Input
          placeholder={placeholder}
          value={draftKey}
          onChange={(e) => setDraftKey(e.target.value)}
          style={{ width: 160 }}
        />
        <InputNumber
          min={0}
          placeholder="Rate"
          value={draftRate}
          onChange={(v) => setDraftRate(v)}
        />
        <Button onClick={addEntry}>Add</Button>
      </Space>
    </Space>
  );
}

export default function RateCardsView() {
  const [groups, setGroups] = useState([]);
  const [policyVersion, setPolicyVersion] = useState(null);
  const [corridorsEnabled, setCorridorsEnabled] = useState(false);
  const [exclusiveEnabled, setExclusiveEnabled] = useState(false);
  const [seasonEnabled, setSeasonEnabled] = useState(false);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [seasonBriefing, setSeasonBriefing] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState("local_intra");
  const [rows, setRows] = useState([]);
  const [params, setParams] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [engineBehaviour, setEngineBehaviour] = useState(null);
  const [editable, setEditable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [filterOrigin, setFilterOrigin] = useState(null);
  const [filterDest, setFilterDest] = useState(null);
  const [filterActive, setFilterActive] = useState("all");
  const [testOpen, setTestOpen] = useState(false);
  const [liveElementsRefreshKey, setLiveElementsRefreshKey] = useState(0);
  // ADR-003 dual-path retired — UI always edits ADR-008 named cards.
  const methodologyEnabled = true;
  const [liveCardId, setLiveCardId] = useState(null);
  const [editingCardId, setEditingCardId] = useState(null);
  const [namedCards, setNamedCards] = useState([]);
  const [cardBusy, setCardBusy] = useState(false);

  const loadGroups = useCallback(async () => {
    const data = await fetchRateCardGroups();
    setGroups((data.groups || []).filter((g) => g.id !== "adr008_live"));
    setPolicyVersion(data.policyVersion || null);
    setCorridorsEnabled(Boolean(data.corridorsEnabled));
    setExclusiveEnabled(Boolean(data.exclusiveEnabled));
    setSeasonEnabled(Boolean(data.seasonEnabled));
    setCurrentSeason(data.season || null);
    setSeasonBriefing(data.seasonBriefing || null);
    setLiveCardId(data.liveCardId || null);
    setEditingCardId(data.editingCardId || data.liveCardId || null);
    setNamedCards(data.cards || []);
  }, []);

  const loadGroup = useCallback(async (groupId, cardId) => {
    const data = await fetchGroupRows(groupId, { cardId });
    setRows(
      (data.rows || []).map((row, index) => ({
        ...row,
        _key: rowKey(row, index),
        season: row.season || "",
        direction: row.direction || "",
      }))
    );
    setParams(data.params || null);
    setPolicy(data.policy || null);
    setEngineBehaviour(data.engineBehaviour || null);
    setEditable(data.editable !== false);
    setDirty(false);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRateCardGroups();
      setGroups((data.groups || []).filter((g) => g.id !== "adr008_live"));
      setPolicyVersion(data.policyVersion || null);
      setCorridorsEnabled(Boolean(data.corridorsEnabled));
      setExclusiveEnabled(Boolean(data.exclusiveEnabled));
      setSeasonEnabled(Boolean(data.seasonEnabled));
      setCurrentSeason(data.season || null);
      setSeasonBriefing(data.seasonBriefing || null);
      setLiveCardId(data.liveCardId || null);
      const nextEditing = data.editingCardId || data.liveCardId || null;
      setEditingCardId(nextEditing);
      setNamedCards(data.cards || []);
      setLiveElementsRefreshKey((key) => key + 1);
      if (selectedGroupId !== LIVE_ELEMENTS_GROUP_ID) {
        await loadGroup(selectedGroupId, nextEditing);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [loadGroup, selectedGroupId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const displayGroups = useMemo(
    () => [LIVE_ELEMENTS_GROUP, ...groups],
    [groups],
  );

  const selectedGroup = useMemo(() => {
    if (selectedGroupId === LIVE_ELEMENTS_GROUP_ID) return LIVE_ELEMENTS_GROUP;
    return groups.find((g) => g.id === selectedGroupId) || null;
  }, [groups, selectedGroupId]);

  const isLiveElementsTab = selectedGroupId === LIVE_ELEMENTS_GROUP_ID;

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (
        selectedGroupId === "shared_load" ||
        selectedGroupId === "exclusive_ld"
      ) {
        if (
          filterOrigin &&
          String(row.originRegion).toUpperCase() !== filterOrigin
        ) {
          return false;
        }
        if (
          filterDest &&
          String(row.destinationRegion).toUpperCase() !== filterDest
        ) {
          return false;
        }
      }
      if (filterActive === "active" && row.enabled === false) return false;
      if (filterActive === "inactive" && row.enabled !== false) return false;
      return true;
    });
  }, [rows, filterOrigin, filterDest, filterActive, selectedGroupId]);

  const updateRow = (key, patch) => {
    setRows((prev) =>
      prev.map((row) => (rowKey(row) === key ? { ...row, ...patch } : row))
    );
    setDirty(true);
  };

  const updateParams = (patch) => {
    setParams((prev) => ({ ...(prev || {}), ...patch }));
    setDirty(true);
  };

  const handleAddRow = () => {
    if (selectedGroupId === "shared_load" && methodologyEnabled) {
      setRows((prev) => [...prev, newAdr008RouteRow()]);
      setDirty(true);
      message.success("Blank shared-route row added — fill regions & tariff, then Save");
    } else if (selectedGroupId === "shared_load") {
      setRows((prev) => [...prev, newSharedRow()]);
      setDirty(true);
      message.success("Blank corridor row added — fill regions & rates, then Save");
    } else if (selectedGroupId === "local_intra" && methodologyEnabled) {
      setRows((prev) => [
        ...prev,
        {
          _key: `truck-${Date.now()}`,
          id: "",
          size: "",
          label: "",
          mileageRate: 0,
          minimumAmount: 0,
          enabled: true,
        },
      ]);
      setDirty(true);
      message.success("Blank truck mileage/minimum row added — then Save");
    } else if (selectedGroupId === "local_intra") {
      setRows((prev) => [...prev, newBandRow()]);
      setDirty(true);
      message.success("Blank vehicle band added — fill size & base, then Save");
    } else if (selectedGroupId === "exclusive_ld") {
      setRows((prev) => [...prev, newExclusiveRow()]);
      setDirty(true);
      message.success("Blank exclusive row added — fill regions & flat rate, then Save");
    }
  };

  const handleDuplicate = (key) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => rowKey(r) === key);
      if (idx < 0) return prev;
      const copy = {
        ...prev[idx],
        _key: `dup-${Date.now()}`,
        id: prev[idx].id ? `${prev[idx].id}-copy` : "",
        size: prev[idx].size ? `${prev[idx].size}_copy` : prev[idx].size,
      };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
    setDirty(true);
    message.info("Row duplicated — edit the copy, then Save");
  };

  const handleRemoveRow = (key) => {
    setRows((prev) => prev.filter((r) => rowKey(r) !== key));
    setDirty(true);
    message.success("Row removed — Save to persist");
  };

  const rowActionsColumn = {
    title: "Actions",
    key: "actions",
    width: 110,
    fixed: "right",
    render: (_, record) => (
      <Space size={0}>
        <Tooltip title="Duplicate this row">
          <Button
            type="text"
            icon={<CopyOutlined />}
            onClick={() => handleDuplicate(rowKey(record))}
            aria-label="Duplicate row"
            data-testid="rate-card-duplicate-row"
          />
        </Tooltip>
        <Popconfirm
          title="Remove this rate row?"
          description="Unsaved until you click Save changes."
          okText="Remove"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
          onConfirm={() => handleRemoveRow(rowKey(record))}
        >
          <Tooltip title="Remove row">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              aria-label="Remove row"
              data-testid="rate-card-remove-row"
            />
          </Tooltip>
        </Popconfirm>
      </Space>
    ),
  };

  const handleSave = async () => {
    if (!editable) return;
    setSaving(true);
    try {
      const payload = rows.map(({ _key, ...rest }) => rest);
      await saveGroupRows(selectedGroupId, {
        rows: payload,
        params,
        cardId: editingCardId,
      });
      message.success(`${selectedGroup?.name || "Rate card"} saved`);
      await refresh();
    } catch (err) {
      message.error(err?.response?.data?.error || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sharedColumns = [
    {
      title: "Origin",
      dataIndex: "originRegion",
      width: 90,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) =>
            updateRow(rowKey(record), {
              originRegion: e.target.value.toUpperCase(),
            })
          }
        />
      ),
    },
    {
      title: "Destination",
      dataIndex: "destinationRegion",
      width: 110,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) =>
            updateRow(rowKey(record), {
              destinationRegion: e.target.value.toUpperCase(),
            })
          }
        />
      ),
    },
    {
      title: "Season",
      dataIndex: "season",
      width: 110,
      render: (value, record) => (
        <Select
          allowClear
          placeholder="—"
          style={{ width: "100%" }}
          value={value || undefined}
          onChange={(v) => updateRow(rowKey(record), { season: v || "" })}
          options={[
            { value: "peak", label: "Peak" },
            { value: "off_peak", label: "Off-peak" },
            { value: "mid_month", label: "Mid-month (off-peak)" },
            { value: "month_end", label: "Month-end (elevated)" },
            { value: "standard", label: "Standard" },
          ]}
        />
      ),
    },
    {
      title: "Direction",
      dataIndex: "direction",
      width: 120,
      render: (value, record) => (
        <Select
          allowClear
          placeholder="—"
          style={{ width: "100%" }}
          value={value || undefined}
          onChange={(v) => updateRow(rowKey(record), { direction: v || "" })}
          options={[
            { value: "outbound", label: "Outbound" },
            { value: "return", label: "Return" },
          ]}
        />
      ),
    },
    {
      title: "Rate / m³",
      dataIndex: "ratePerM3",
      width: 110,
      render: (value, record) => (
        <InputNumber
          min={0}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) => updateRow(rowKey(record), { ratePerM3: v ?? 0 })}
        />
      ),
    },
    {
      title: "Minimum",
      dataIndex: "minimumAmount",
      width: 110,
      render: (value, record) => (
        <InputNumber
          min={0}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) =>
            updateRow(rowKey(record), { minimumAmount: v ?? 0 })
          }
        />
      ),
    },
    {
      title: "Depo-to-depo (R)",
      dataIndex: "depoToDepoAmount",
      width: 130,
      render: (value, record) => (
        <InputNumber
          min={0}
          placeholder="N/A"
          value={value == null ? null : value}
          style={{ width: "100%" }}
          onChange={(v) =>
            updateRow(rowKey(record), {
              depoToDepoAmount: v == null || v === "" ? null : v,
            })
          }
        />
      ),
    },
    {
      title: "Active",
      dataIndex: "enabled",
      width: 80,
      render: (value, record) => (
        <Switch
          checked={value !== false}
          onChange={(checked) =>
            updateRow(rowKey(record), { enabled: checked })
          }
        />
      ),
    },
    rowActionsColumn,
  ];

  const localColumns = [    {
      title: "Size id",
      dataIndex: "size",
      width: 110,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) =>
            updateRow(rowKey(record), {
              size: e.target.value,
              id: e.target.value,
            })
          }
        />
      ),
    },
    {
      title: "Label",
      dataIndex: "label",
      width: 120,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) => updateRow(rowKey(record), { label: e.target.value })}
        />
      ),
    },
    {
      title: "Customer label",
      dataIndex: "customerLabel",
      width: 130,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) =>
            updateRow(rowKey(record), { customerLabel: e.target.value })
          }
        />
      ),
    },
    {
      title: "Usable m³",
      dataIndex: "usableM3",
      width: 100,
      render: (value, record) => (
        <InputNumber
          min={0}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) => updateRow(rowKey(record), { usableM3: v ?? 0 })}
        />
      ),
    },
    {
      title: "Base (R)",
      dataIndex: "baseAmount",
      width: 110,
      render: (value, record) => (
        <InputNumber
          min={0}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) => updateRow(rowKey(record), { baseAmount: v ?? 0 })}
        />
      ),
    },
    {
      title: "Drivers",
      dataIndex: "includedDrivers",
      width: 90,
      render: (value, record) => (
        <InputNumber
          min={0}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) =>
            updateRow(rowKey(record), { includedDrivers: v ?? 0 })
          }
        />
      ),
    },
    {
      title: "Helpers",
      dataIndex: "includedHelpers",
      width: 90,
      render: (value, record) => (
        <InputNumber
          min={0}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) =>
            updateRow(rowKey(record), { includedHelpers: v ?? 0 })
          }
        />
      ),
    },
    {
      title: "Active",
      dataIndex: "enabled",
      width: 80,
      render: (value, record) => (
        <Switch
          checked={value !== false}
          onChange={(checked) =>
            updateRow(rowKey(record), { enabled: checked })
          }
        />
      ),
    },
    rowActionsColumn,
  ];

  const accessColumns = [
    {
      title: "Adjustment",
      dataIndex: "label",
      width: 180,
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: "Condition",
      dataIndex: "condition",
      render: (value, record) => (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Text type="secondary">{value}</Text>
          {record.id === "long_carry" && (
            <InputNumber
              addonBefore="Threshold m"
              min={0}
              value={record.conditionMetres}
              onChange={(v) =>
                updateRow(rowKey(record), { conditionMetres: v ?? 0 })
              }
            />
          )}
        </Space>
      ),
    },
    {
      title: "Unit",
      dataIndex: "unit",
      width: 90,
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: "Price (R)",
      dataIndex: "amount",
      width: 120,
      render: (value, record) => (
        <InputNumber
          min={0}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) => updateRow(rowKey(record), { amount: v ?? 0 })}
        />
      ),
    },
    {
      title: "Active",
      dataIndex: "enabled",
      width: 80,
      render: (value, record) => (
        <Switch
          checked={value !== false}
          onChange={(checked) =>
            updateRow(rowKey(record), { enabled: checked })
          }
        />
      ),
    },
  ];

  const jobExtrasColumns = accessColumns;

  const addonColumns = [
    {
      title: "SKU",
      dataIndex: "sku",
      width: 150,
      render: (value) => <Text code>{value}</Text>,
    },
    {
      title: "PWA key",
      dataIndex: "pwaKey",
      width: 140,
      render: (value) => <Text type="secondary">{value}</Text>,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) => updateRow(rowKey(record), { name: e.target.value })}
        />
      ),
    },
    {
      title: "Unit",
      dataIndex: "unitLabel",
      width: 90,
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: "Unit price (R)",
      dataIndex: "unitPrice",
      width: 130,
      render: (value, record) => (
        <InputNumber
          min={0}
          step={0.01}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) => updateRow(rowKey(record), { unitPrice: v ?? 0 })}
        />
      ),
    },
    {
      title: "Active",
      dataIndex: "enabled",
      width: 80,
      render: (value, record) => (
        <Switch
          checked={value !== false}
          onChange={(checked) =>
            updateRow(rowKey(record), { enabled: checked })
          }
        />
      ),
    },
  ];

  const exclusiveColumns = [
    {
      title: "Origin",
      dataIndex: "originRegion",
      width: 90,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) =>
            updateRow(rowKey(record), {
              originRegion: e.target.value.toUpperCase(),
            })
          }
        />
      ),
    },
    {
      title: "Destination",
      dataIndex: "destinationRegion",
      width: 110,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) =>
            updateRow(rowKey(record), {
              destinationRegion: e.target.value.toUpperCase(),
            })
          }
        />
      ),
    },
    {
      title: "Truck size",
      dataIndex: "truckSize",
      width: 110,
      render: (value, record) => (
        <Input
          value={value}
          placeholder="8t"
          onChange={(e) =>
            updateRow(rowKey(record), { truckSize: e.target.value })
          }
        />
      ),
    },
    {
      title: "Flat (R)",
      dataIndex: "flatAmount",
      width: 120,
      render: (value, record) => (
        <InputNumber
          min={0}
          placeholder={record.placeholder ? "Set rate" : undefined}
          value={value == null ? null : value}
          style={{ width: "100%" }}
          onChange={(v) =>
            updateRow(rowKey(record), {
              flatAmount: v == null || v === "" ? null : v,
              placeholder:
                v == null || v === "" || !(Number(v) > 0)
                  ? true
                  : false,
            })
          }
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "placeholder",
      width: 110,
      render: (value, record) =>
        value || record.flatAmount == null ? (
          <Tag color="default">Placeholder</Tag>
        ) : (
          <Tag color="green">Priced</Tag>
        ),
    },
    {
      title: "Minimum",
      dataIndex: "minimumAmount",
      width: 110,
      render: (value, record) => (
        <InputNumber
          min={0}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) =>
            updateRow(rowKey(record), { minimumAmount: v ?? 0 })
          }
        />
      ),
    },
    {
      title: "Active",
      dataIndex: "enabled",
      width: 80,
      render: (value, record) => (
        <Switch
          checked={value !== false && Number(record.flatAmount) > 0}
          disabled={record.flatAmount == null || !(Number(record.flatAmount) > 0)}
          onChange={(checked) =>
            updateRow(rowKey(record), { enabled: checked })
          }
        />
      ),
    },
    rowActionsColumn,
  ];

  const adr008Columns = [
    {
      title: "Origin",
      dataIndex: "originRegion",
      width: 90,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) =>
            updateRow(rowKey(record), {
              originRegion: e.target.value.toUpperCase(),
            })
          }
        />
      ),
    },
    {
      title: "Destination",
      dataIndex: "destinationRegion",
      width: 110,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) =>
            updateRow(rowKey(record), {
              destinationRegion: e.target.value.toUpperCase(),
            })
          }
        />
      ),
    },
    {
      title: "Season",
      dataIndex: "season",
      width: 130,
      render: (value, record) => (
        <Select
          allowClear
          placeholder="Any"
          style={{ width: "100%" }}
          value={value || undefined}
          onChange={(v) => updateRow(rowKey(record), { season: v || "" })}
          options={[
            { value: "standard", label: "Standard" },
            { value: "mid_month", label: "Mid-month" },
            { value: "month_end", label: "Month-end" },
            { value: "peak", label: "Peak" },
            { value: "off_peak", label: "Off-peak" },
          ]}
        />
      ),
    },
    {
      title: "Face rate (R)",
      dataIndex: "rate",
      width: 120,
      render: (value, record) => (
        <InputNumber
          min={0}
          step={0.1}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) => updateRow(rowKey(record), { rate: v ?? 0 })}
        />
      ),
    },
    {
      title: "Tariff basis",
      dataIndex: "tariffBasis",
      width: 150,
      render: (value, record) => (
        <Select
          allowClear
          placeholder="Card default"
          style={{ width: "100%" }}
          value={value || undefined}
          onChange={(v) => updateRow(rowKey(record), { tariffBasis: v || "" })}
          options={[
            { value: "UNCONFIRMED", label: "UNCONFIRMED" },
            { value: "PER_FT3", label: "PER_FT3" },
            { value: "PER_100_FT3", label: "PER_100_FT3" },
          ]}
        />
      ),
    },
    {
      title: "Mileage R/km",
      dataIndex: "mileageRate",
      width: 120,
      render: (value, record) => (
        <InputNumber
          min={0}
          step={0.1}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) => updateRow(rowKey(record), { mileageRate: v ?? 0 })}
        />
      ),
    },
    {
      title: "Depo-to-depo (stored)",
      dataIndex: "depoToDepoAmount",
      width: 150,
      render: (value, record) => (
        <InputNumber
          min={0}
          value={value == null ? null : value}
          style={{ width: "100%" }}
          onChange={(v) =>
            updateRow(rowKey(record), {
              depoToDepoAmount: v == null || v === "" ? null : v,
            })
          }
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "placeholder",
      width: 110,
      render: (value, record) =>
        value === true || record.enabled === false ? (
          <Tag color="default">
            {value === true ? "Provisional" : "Disabled"}
          </Tag>
        ) : (
          <Tag color="green">Live</Tag>
        ),
    },
    {
      title: "Active",
      dataIndex: "enabled",
      width: 80,
      render: (value, record) => (
        <Switch
          checked={value !== false}
          onChange={(checked) =>
            updateRow(rowKey(record), {
              enabled: checked,
              ...(checked ? { placeholder: false } : {}),
            })
          }
        />
      ),
    },
    rowActionsColumn,
  ];

  const localTruckColumns = [
    {
      title: "Truck size",
      dataIndex: "size",
      width: 140,
      render: (value, record) => (
        <Input
          value={value}
          placeholder="6_ton"
          onChange={(e) => {
            const size = e.target.value.trim();
            updateRow(rowKey(record), { size, id: size, label: size });
          }}
        />
      ),
    },
    {
      title: "Mileage R/km",
      dataIndex: "mileageRate",
      width: 130,
      render: (value, record) => (
        <InputNumber
          min={0}
          step={0.1}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) => updateRow(rowKey(record), { mileageRate: v ?? 0 })}
        />
      ),
    },
    {
      title: "Minimum (R)",
      dataIndex: "minimumAmount",
      width: 130,
      render: (value, record) => (
        <InputNumber
          min={0}
          value={value}
          style={{ width: "100%" }}
          onChange={(v) =>
            updateRow(rowKey(record), { minimumAmount: v ?? 0 })
          }
        />
      ),
    },
    {
      title: "Active",
      dataIndex: "enabled",
      width: 80,
      render: (value, record) => (
        <Switch
          checked={value !== false}
          onChange={(checked) =>
            updateRow(rowKey(record), { enabled: checked })
          }
        />
      ),
    },
    rowActionsColumn,
  ];

  const columns =
    selectedGroupId === "shared_load" && methodologyEnabled
      ? adr008Columns
      : selectedGroupId === "local_intra" && methodologyEnabled
        ? localTruckColumns
        : selectedGroupId === "local_intra"
          ? localColumns
          : selectedGroupId === "access_handling"
            ? accessColumns
            : selectedGroupId === "job_extras"
              ? jobExtrasColumns
              : selectedGroupId === "addons"
                ? addonColumns
                : selectedGroupId === "exclusive_ld"
                  ? exclusiveColumns
                  : sharedColumns;

  const canAddRow =
    editable &&
    (selectedGroupId === "shared_load" ||
      selectedGroupId === "local_intra" ||
      selectedGroupId === "exclusive_ld");

  return (
    <PageShell data-testid="rate-cards-page" sx={{ maxWidth: 1400, mx: "auto" }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            Pricing
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            One ADR-008-compatible named card is live at a time. Tabs edit that
            card’s Local, Shared, Exclusive, Access, Extras and packaging rates.
            Use Live elements for the PWA / quote / invoice contract. Duplicate
            to draft, then Set live.
          </Paragraph>
        </div>
        <Space wrap>
          <Tag color="green" data-testid="adr008-live-card-tag">
            Live card{liveCardId ? `: ${liveCardId}` : ""}
          </Tag>
          {policyVersion && (
            <Tag color="blue">Active version: {String(policyVersion)}</Tag>
          )}
          {!corridorsEnabled && (
            <Tag color="orange">Corridor pricing OFF in settings</Tag>
          )}
          {!exclusiveEnabled && (
            <Tag color="orange">Exclusive LD pricing OFF in settings</Tag>
          )}
          {seasonEnabled ? (
            <Tag color="blue">
              Season: {currentSeason?.label || currentSeason?.seasonCode || "—"}
            </Tag>
          ) : (
            <Tag color="default">Season calendar OFF</Tag>
          )}
          <Button
            icon={<ExperimentOutlined />}
            onClick={() => setTestOpen(true)}
            data-testid="open-test-pricing"
          >
            Test Pricing
          </Button>
          <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
            Refresh
          </Button>
        </Space>
      </Space>

      {seasonBriefing ? (
        <div style={{ marginTop: 16 }}>
          <SeasonOpsBrief briefing={seasonBriefing} />
        </div>
      ) : null}

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginTop: 16 }}
        />
      )}

      <Card
        size="small"
        style={{ marginTop: 16 }}
        title="Price card (ADR-008)"
        data-testid="named-rate-card-picker"
      >
        <Space wrap align="center">
          <span>
            Editing{" "}
            <Select
              style={{ minWidth: 220 }}
              value={editingCardId || undefined}
              placeholder="Select card"
              options={(namedCards || []).map((c) => ({
                value: c.id,
                label: `${c.id}${c.live ? " (live)" : ""}${
                  c.version ? ` · ${c.version}` : ""
                }`,
              }))}
              onChange={async (cardId) => {
                if (dirty) {
                  message.warning("Save or discard changes before switching cards");
                  return;
                }
                setCardBusy(true);
                try {
                  await setEditingRateCard(cardId);
                  setEditingCardId(cardId);
                  if (selectedGroupId !== LIVE_ELEMENTS_GROUP_ID) {
                    await loadGroup(selectedGroupId, cardId);
                  } else {
                    setLiveElementsRefreshKey((key) => key + 1);
                  }
                  message.success(`Editing card ${cardId}`);
                } catch (err) {
                  message.error(
                    err?.response?.data?.error || err.message || "Switch failed"
                  );
                } finally {
                  setCardBusy(false);
                }
              }}
              data-testid="named-card-select"
            />
          </span>
          <Tag color={editingCardId && editingCardId === liveCardId ? "green" : "gold"}>
            {editingCardId && editingCardId === liveCardId
              ? "Editing live card"
              : "Draft (not live)"}
          </Tag>
          <Button
            loading={cardBusy}
            disabled={!editingCardId || editingCardId === liveCardId}
            onClick={async () => {
              setCardBusy(true);
              try {
                await setLiveRateCard(editingCardId);
                await refresh();
                message.success(`Live card set to ${editingCardId}`);
              } catch (err) {
                message.error(
                  err?.response?.data?.error || err.message || "Set live failed"
                );
              } finally {
                setCardBusy(false);
              }
            }}
            data-testid="named-card-set-live"
          >
            Set live
          </Button>
          <Button
            loading={cardBusy}
            disabled={!editingCardId}
            onClick={async () => {
              const newId = window.prompt(
                "New card id (slug)",
                `${editingCardId || "card"}-copy`
              );
              if (!newId) return;
              setCardBusy(true);
              try {
                await duplicateRateCard({
                  sourceId: editingCardId,
                  newId: String(newId).trim(),
                });
                await refresh();
                message.success(`Duplicated to ${String(newId).trim()}`);
              } catch (err) {
                message.error(
                  err?.response?.data?.error || err.message || "Duplicate failed"
                );
              } finally {
                setCardBusy(false);
              }
            }}
            data-testid="named-card-duplicate"
          >
            Duplicate card
          </Button>
        </Space>
      </Card>

      <Row gutter={16} style={{ marginTop: 20 }}>
        <Col xs={24} md={7} lg={6}>
          <Card title="Rate card groups" size="small">
            <GroupList>
              {displayGroups.map((group) => {
                const active = group.id === selectedGroupId;
                return (
                  <GroupButton
                    key={group.id}
                    type="button"
                    $active={active}
                    onClick={() => setSelectedGroupId(group.id)}
                    data-testid={`rate-card-group-${group.id}`}
                  >
                    <div style={{ fontWeight: 600 }}>{group.name}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {group.methodLabel}
                      {group.rows != null
                        ? ` · ${group.activeRows ?? 0} active`
                        : ""}
                    </Text>
                    {group.status === "legacy" && (
                      <Tag style={{ marginTop: 6 }} color="gold">
                        Legacy
                      </Tag>
                    )}
                    {group.editable === false && group.id !== LIVE_ELEMENTS_GROUP_ID && (
                      <div>
                        <Tag style={{ marginTop: 6 }}>Policy</Tag>
                      </div>
                    )}
                  </GroupButton>
                );
              })}
            </GroupList>
          </Card>
        </Col>

        <Col xs={24} md={17} lg={18}>
          {isLiveElementsTab ? (
            <LivePriceCardElementsPanel refreshKey={liveElementsRefreshKey} />
          ) : (
          <Card
            title={selectedGroup?.name || "Rate card"}
            size="small"
            extra={
              editable ? (
                <Space wrap>
                  {canAddRow && (
                    <Tooltip title="Insert a blank rate row (not a copy)">
                      <Button
                        type="default"
                        icon={<PlusOutlined />}
                        onClick={handleAddRow}
                        data-testid="rate-card-add-row"
                        style={{
                          borderColor: "#1677ff",
                          color: "#1677ff",
                          fontWeight: 600,
                        }}
                      >
                        Add blank row
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    disabled={!dirty}
                    onClick={handleSave}
                    data-testid="save-rate-card-rows"
                  >
                    Save changes
                  </Button>
                </Space>
              ) : null
            }
          >
            {engineBehaviour && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="How this feeds the engine"
                description={
                  <div>
                    <div>
                      <Text strong>{selectedGroup?.methodLabel}</Text>
                    </div>
                    <div>Input: {engineBehaviour.input}</div>
                    <div>
                      Match: {(engineBehaviour.match || []).join("; ")}
                    </div>
                    <div>
                      Calculation:{" "}
                      <Text code>{engineBehaviour.calculation}</Text>
                    </div>
                  </div>
                }
              />
            )}

            {policy && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={policy.title}
                description={policy.body}
              />
            )}

            {selectedGroupId === "shared_load" && methodologyEnabled && params && (
              <Card
                size="small"
                type="inner"
                title={`Shared rates on card “${editingCardId || params.cardId || "unnamed"}”`}
                style={{ marginBottom: 16 }}
                data-testid="card-shared-params"
              >
                <Space wrap>
                  <span>
                    Version{" "}
                    <Input
                      value={params.version}
                      style={{ width: 140 }}
                      onChange={(e) =>
                        updateParams({ version: e.target.value })
                      }
                    />
                  </span>
                  <span>
                    Default tariff basis{" "}
                    <Select
                      style={{ width: 170 }}
                      value={params.tariffBasis || "UNCONFIRMED"}
                      onChange={(v) => updateParams({ tariffBasis: v })}
                      options={[
                        { value: "UNCONFIRMED", label: "UNCONFIRMED" },
                        { value: "PER_FT3", label: "PER_FT3" },
                        { value: "PER_100_FT3", label: "PER_100_FT3" },
                      ]}
                    />
                  </span>
                  <span>
                    Hundreds rounding{" "}
                    <Select
                      style={{ width: 120 }}
                      value={params.hundredsRounding || "EXACT"}
                      onChange={(v) => updateParams({ hundredsRounding: v })}
                      options={[
                        { value: "EXACT", label: "EXACT" },
                        { value: "CEIL", label: "CEIL" },
                      ]}
                    />
                  </span>
                  <span>
                    Collection grace km{" "}
                    <InputNumber
                      min={0}
                      value={params.collectionGraceKm}
                      onChange={(v) =>
                        updateParams({ collectionGraceKm: v ?? 0 })
                      }
                    />
                  </span>
                  <span>
                    Delivery grace km{" "}
                    <InputNumber
                      min={0}
                      value={params.deliveryGraceKm}
                      onChange={(v) =>
                        updateParams({ deliveryGraceKm: v ?? 0 })
                      }
                    />
                  </span>
                </Space>
                {params.tariffBasis === "UNCONFIRMED" && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                    message="UNCONFIRMED tariff basis"
                    description="Shared line haul will not emit money until PER_FT3 or PER_100_FT3 is set. PER_FT3 billed in hundreds is 100× PER_100_FT3 for the same face rate."
                  />
                )}
                <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                  Shared routes use 100 ft³ line haul plus collection/delivery.
                  depo-to-depo amounts are stored and ignored for line haul.
                  Local volume/mileage lives on the Local tab of this same card.
                </Paragraph>
              </Card>
            )}

            {selectedGroupId === "local_intra" && methodologyEnabled && params && (
              <Card
                size="small"
                type="inner"
                title={`Local rates on card “${editingCardId || params.cardId || "unnamed"}”`}
                style={{ marginBottom: 16 }}
                data-testid="card-local-params"
              >
                <Space wrap>
                  <span>
                    Version{" "}
                    <Input
                      value={params.version}
                      style={{ width: 140 }}
                      onChange={(e) =>
                        updateParams({ version: e.target.value })
                      }
                    />
                  </span>
                  <span>
                    Local volume max km{" "}
                    <InputNumber
                      min={1}
                      value={params.localVolumeMaxKm}
                      onChange={(v) =>
                        updateParams({ localVolumeMaxKm: v ?? 40 })
                      }
                    />
                  </span>
                  <span>
                    Mid-month R/ft³{" "}
                    <InputNumber
                      min={0}
                      step={0.1}
                      value={params.volumeMidMonth}
                      onChange={(v) =>
                        updateParams({ volumeMidMonth: v ?? 0 })
                      }
                    />
                  </span>
                  <span>
                    Month-end R/ft³{" "}
                    <InputNumber
                      min={0}
                      step={0.1}
                      value={params.volumeMonthEnd}
                      onChange={(v) =>
                        updateParams({ volumeMonthEnd: v ?? 0 })
                      }
                    />
                  </span>
                </Space>
                <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                  ≤ max km → volume only. Above max km → full-route mileage only
                  (table rows). Optional truck minimum floors the transport line.
                </Paragraph>
              </Card>
            )}

            {selectedGroupId === "exclusive_ld" && params && (
              <Card
                size="small"
                type="inner"
                title="Exclusive LD automation"
                style={{ marginBottom: 16 }}
              >
                <Space wrap>
                  <span>
                    Enable exclusive pricing{" "}
                    <Switch
                      checked={params.exclusiveEnabled === true}
                      onChange={(checked) =>
                        updateParams({ exclusiveEnabled: checked })
                      }
                    />
                  </span>
                  <Text type="secondary">
                    OFF keeps exclusive_policy_pending (manual). ON uses the
                    catalogue below.
                  </Text>
                </Space>
              </Card>
            )}

            <>
              <Space wrap style={{ marginBottom: 12 }}>
                {(selectedGroupId === "shared_load" ||
                  selectedGroupId === "exclusive_ld" ||
                  methodologyEnabled) && (
                  <>
                    <Input
                      placeholder="Filter origin"
                      allowClear
                      style={{ width: 120 }}
                      onChange={(e) =>
                        setFilterOrigin(
                          e.target.value
                            ? e.target.value.trim().toUpperCase()
                            : null
                        )
                      }
                    />
                    <Input
                      placeholder="Filter destination"
                      allowClear
                      style={{ width: 140 }}
                      onChange={(e) =>
                        setFilterDest(
                          e.target.value
                            ? e.target.value.trim().toUpperCase()
                            : null
                        )
                      }
                    />
                  </>
                )}
                <Select
                  style={{ width: 130 }}
                  value={filterActive}
                  onChange={setFilterActive}
                  options={[
                    { value: "all", label: "All status" },
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                />
                {dirty && <Tag color="gold">Unsaved changes</Tag>}
              </Space>

              <Table
                size="small"
                loading={loading}
                rowKey={(r) => rowKey(r)}
                dataSource={filteredRows}
                columns={columns}
                pagination={{ pageSize: 20 }}
                scroll={{ x: 900 }}
              />
              {canAddRow ? (
                <Button
                  block
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleAddRow}
                  data-testid="rate-card-add-row-footer"
                  style={{ marginTop: 12 }}
                >
                  Add blank row
                </Button>
              ) : null}
            </>
          </Card>
          )}
        </Col>
      </Row>

      <TestPricingDrawer open={testOpen} onClose={() => setTestOpen(false)} />
    </PageShell>
  );
}
